import request from "@/app/api/request";
import {
  NearbyUsersModel,
  NewUserModel, NotificationModel, PreordersEmailModel,
  UpdateUserLocationModel,
  UserChangeGeoSettingsModel,
  UserModel, UserProfileModelForSend,
  PlayingFrequencyEnum
} from "@/app/api/user/models";
import Cookies from "js-cookie";
import {CookiesKey} from "@/app/utils/cookies";
import {fromEnumKeyToEnumValue} from "@/app/utils/custom_function";

export class UserRequests {
  static async updateUser(model: UserProfileModelForSend): Promise<UserModel> {
    try {
      return await request.post<UserModel>('/user/editUserProfile', {data: model});
    } catch (error: any) {
      throw error;
    }
  }

  static async uploadImage(model: FormData): Promise<string> {
    try {
      return await request.post<string>('/user/uploadFileToS3', {
        data: model,
        config: {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      });
    } catch (error: any) {
      throw error;
    }
  }

  static async changeGeoSetting(model: UserChangeGeoSettingsModel): Promise<any> {
    try {
      return await request.patch<any>('/user/changeGeoSettings', {data: model});
    } catch (error: any) {
      throw error;
    }
  }

  static async changeUserLocation(model: UpdateUserLocationModel): Promise<any> {
    try {
      return await request.patch<any>('/user/updateUserLocation', {
        // data: {
        //   latitude: "50.457520",
        //   longitude: "30.410882"
        // }
        data: model
      });
    } catch (error: any) {
      throw error;
    }
  }

  static async getCurrentUser() {
    try {
      const res = await request.get<any>('user/getCurrentUser', {});

      // Always store raw API response in cookie for parts of the app
      // that rely on the server shape (e.g. ProtectedRoute, admin).
      Cookies.set(CookiesKey.user, JSON.stringify(res));

      // If API already returns a NewUserModel with nested user, use it as-is
      if (res && typeof res === 'object' && res.user) {
        return res as NewUserModel;
      }

      // Normalize legacy / admin-style flat response with optional profile field
      const profile = res?.profile ?? {};

      // Prefer top-level playFrequency from user, fallback to profile.playingFrequency
      const playingFrequencyKey: string | undefined =
        res.playFrequency ?? profile.playingFrequency;

      const playingFrequency: PlayingFrequencyEnum | undefined = playingFrequencyKey
        ? fromEnumKeyToEnumValue(PlayingFrequencyEnum, playingFrequencyKey as string) as PlayingFrequencyEnum
        : undefined;

      const normalized: NewUserModel = {
        id: res.id,
        user: {
          id: res.id,
          email: res.email,
          password: undefined,
          username: res.username,
          imageLink: res.imageLink,
          lastLat: res.lastLat,
          lastLng: res.lastLng,
          locationUpdatedAt: res.locationUpdatedAt,
          available: res.available,
          skillLevel: res.skillLevel ?? profile.skillLevel,
          experienceYears: res.experienceYears,
          playFrequency: playingFrequency,
          matchesCount: res.matchesCount,
          preferredRadiusKm: res.preferredRadiusKm,
          referrals: Array.isArray(res.referrals) ? res.referrals : undefined,
          referralsCount: res.referralsCount,
        },
        rocketSportsExperience: profile.rocketSportsExperience,
        ntrpRating: profile.ntrpRating,
        skillLevel: profile.skillLevel ?? res.skillLevel,
        city: profile.city,
        gender: profile.gender,
        showGenderInProfile: profile.showGenderInProfile,
        dateOfBirth: profile.dateOfBirth,
        padelClubMembership: profile.padelClubMembership,
        playingDuration: profile.playingDuration,
        aboutYourself: profile.aboutYourself,
        industry: profile.industry,
        instagram: profile.instagram,
        linkedin: profile.linkedin,
        profile: res.profile ?? null,
      };

      return normalized;
    } catch (error: any) {
      throw error;
    }
  }

  static async getUserById(userId: number) {
    try {
      const res = await request.get<any>('user/getUserById', {query: {userId}});

      // If API already returns a NewUserModel with nested user, use it as-is
      if (res && typeof res === 'object' && res.user) {
        return res as NewUserModel;
      }

      // Normalize new flat response with nested profile
      const profile = res?.profile ?? {};

      // Prefer top-level playFrequency from user, fallback to profile.playingFrequency
      const playingFrequencyKey: string | undefined =
        res.playFrequency ?? profile.playingFrequency;

      const playingFrequency: PlayingFrequencyEnum | undefined = playingFrequencyKey
        ? fromEnumKeyToEnumValue(PlayingFrequencyEnum, playingFrequencyKey as string) as PlayingFrequencyEnum
        : undefined;

      const normalized: NewUserModel = {
        id: res.id,
        user: {
          id: res.id,
          email: res.email,
          password: undefined,
          username: res.username,
          imageLink: res.imageLink,
          lastLat: res.lastLat,
          lastLng: res.lastLng,
          locationUpdatedAt: res.locationUpdatedAt,
          available: res.available,
          skillLevel: res.skillLevel ?? profile.skillLevel,
          experienceYears: res.experienceYears,
          playFrequency: playingFrequency,
          matchesCount: res.matchesCount,
          preferredRadiusKm: res.preferredRadiusKm,
          referrals: Array.isArray(res.referrals) ? res.referrals : undefined,
          referralsCount: res.referralsCount,
        },
        rocketSportsExperience: profile.rocketSportsExperience,
        ntrpRating: profile.ntrpRating,
        skillLevel: profile.skillLevel ?? res.skillLevel,
        city: profile.city,
        gender: profile.gender,
        showGenderInProfile: profile.showGenderInProfile,
        dateOfBirth: profile.dateOfBirth,
        padelClubMembership: profile.padelClubMembership,
        playingDuration: profile.playingDuration,
        aboutYourself: profile.aboutYourself,
        industry: profile.industry,
        instagram: profile.instagram,
        linkedin: profile.linkedin,
        profile: res.profile ?? null,
      };

      return normalized;

    } catch (error: any) {
      throw error;
    }
  }

  static async getUserFromCookie() {
    try {
      // For app logic we always rely on the normalized model
      // and let getCurrentUser handle cookie syncing.
      return await UserRequests.getCurrentUser();
    } catch (error: any) {
      throw error;
    }
  }

  static async getUsersNearby(page: number): Promise<NearbyUsersModel[]> {
    try {
      const users = await request.get<NearbyUsersModel[]>('user/nearbyPlayers', {query: {page}});
      return users;
    } catch (error: any) {
      throw error;
    }
  }

  static async searchUsers(username: string, page: number = 0): Promise<NearbyUsersModel[]> {
    try {
      return await request.get<NearbyUsersModel[]>('user/findUsersByUsername', {
        query: {
          username,
          doNearby: true,
          page
        }
      });
    } catch (error: any) {
      throw error;
    }
  }

  static async getPreordersEmails(): Promise<PreordersEmailModel[]> {
    try {
      return await request.get<PreordersEmailModel[]>('user/getPreordersEmails', {});
    } catch (error: any) {
      throw error;
    }
  }

  static async savePreordersEmail(email: string): Promise<PreordersEmailModel[]> {
    try {
      return await request.post<PreordersEmailModel[]>('user/savePreorderEmail', {query: {email}});
    } catch (error: any) {
      throw error;
    }
  }

  static async getUserNotifications(): Promise<NotificationModel[]> {
    try {
      return await request.get<NotificationModel[]>('user/getUserNotifications', {});
    } catch (error: any) {
      throw error;
    }
  }
}

